<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { eqsApi } from '@/api/eqs';
import StatusChip from '@/components/StatusChip.vue';
import { confirmAction } from '@/services/confirm';
import { notify } from '@/services/notice';
import type { ModuleRecord } from '@/types/domain';
import { eightDLabel, eightDStep } from '@/utils/eight-d';
import { errorMessage } from '@/utils/format';

/**
 * 记录的子条目：8D 步骤、变更措施。
 *
 * 这些数据一直在库里（76 条 8D 步骤、25 条变更措施），但前端从没取过——
 * 模块名叫「问题 / 8D」，点进去却看不到 8D 的任何一步（2026-08-13 盘点发现）。
 *
 * editable 打开后可以添加和修改步骤，但不能删除——8D 是给客户看的记录，
 * 步骤删掉就断档了；不要的步骤走「作废」，留痕但不再算进度。
 */
const props = defineProps<{
  module: string;
  resource: string;
  parentId: string | number;
  title: string;
  /** 空态文案，说明这类子条目是什么 */
  emptyHint: string;
  /** 从 normalized 里额外展示的字段（键 → 中文名） */
  extraFields?: Record<string, string>;
  /**
   * 按 code 分组渲染。传了就只显示命中这些组的条目。
   *
   * 归不进任何一组的条目**不显示**（0827 Tony：「其他措施」这一组撤掉）。
   * 静默过滤本来是这套系统吃过大亏的事（「346 个文件只显示 50 个」），
   * 这次能撤，是因为**同时**把那批条目移出了清单口径：
   * 不计进度、不挡推进（后端 isChecklistCode），并由调用方在清单外
   * 用一句灰字报出条数。藏起来的是一批已经退役的老数据，不是还在起作用的门禁。
   *
   * 不传时行为和分组前**一字不差**（8D 和别的模块都在用这个组件）。
   */
  codeGroups?: Array<{ name: string; codes: string[] }>;
  /**
   * **属于别处**的条目 code：整个不显示，也不算进「其他措施」。
   *
   * 变更的执行清单一次返回整条变更的措施，第 4、5 步各按自己的 groups 分组。
   * 不给这份名单的话，第 5 步的 ST01–ST06 会掉进第 4 步的「其他措施」——
   * 同一批措施两步各显示一遍，还被当成旧 EQS 迁来的垃圾数据（0827 Tony 截图）。
   * 「没归组」和「归在别的步」是两回事，混在一起就是在骗人。
   */
  foreignCodes?: string[];
  /**
   * 标题文字交给外层写。嵌进变更第 4 步的展开区之后，
   * 上面已经有「第 4 步 · 计划与执行落地」和「做完的标准：…」，
   * 再来一个「变更措施 / 执行清单」就是这次改动本来要消掉的那种重复。
   * 只摘标题文字：「共 N 条」和「＋ 添加」照旧留着，它们现在就在抽屉底下能用。
   */
  hideTitle?: boolean;
  /** 允许添加/编辑/作废 */
  editable?: boolean;
  /** 新建时的步骤选项（8D 用 D0–D8）；不传则新建表单不带步骤列 */
  stepOptions?: string[];
  /** 打开时直接展开新建表单并选中这一步（从 8D 报告点某个空缺的 D 步进来） */
  presetStep?: string | null;
  /**
   * 改子条目状态前先让外层处理一下（通常是把抽屉表单里没保存的改动存了）。
   * 返回 false 表示放弃这次操作。
   */
  beforeAdvance?: () => Promise<boolean>;
  /**
   * 措施行带交付物附件（0826 Tony：每个活动任务确认完成前需要交付物）。
   * 打开后每行显示 📎 数、能行内上传，「完成」在前端先查有没有证据
   * （后端 assertActionEvidenceAttached 同一判据再拦一遍）。
   * 附件挂在 module_record_attachments 的 `${module}/${resource}` 这条记录自己身上。
   * 8D 等别的用法不传，行为一字不变。
   */
  withAttachments?: boolean;
  /**
   * 「作废」这个动作在这份清单里叫什么。变更措施 0826 起叫「不适用」——
   * 措施是模板铺的，「这条对本变更不适用」比「作废」贴切；存库状态不变（cancelled）。
   */
  voidLabel?: string;
}>();

const emit = defineEmits<{ changed: [] }>();

/** 「作废」的显示名；8D 等不传时保持原文案 */
const voidText = computed(() => props.voidLabel ?? '作废');

/**
 * 选中的 D 步要填什么。
 *
 * 原来九个步骤共用「内容说明 *」一个单行框，选了 D4 也没有任何提示——
 * 「D4 填什么、和 D3 差在哪」全靠猜（试用反馈 8/14）。
 */
const stepGuide = computed(() => (props.stepOptions?.length ? eightDStep(addStep.value) : null));
function optionLabel(code: string): string {
  return props.stepOptions?.length ? eightDLabel(code) : code;
}

const items = ref<ModuleRecord[]>([]);
const loading = ref(false);
const error = ref('');

async function load(): Promise<void> {
  loading.value = true;
  error.value = '';
  try {
    const payload = await eqsApi.childRecords(props.module, props.resource, props.parentId);
    items.value = payload.items;
  } catch (caught) {
    items.value = [];
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
  }
}

watch(() => [props.module, props.resource, props.parentId], load, { immediate: true });

// 外面写入 D 步之后要能让这块自己刷新（5Why 根因一键写成 D4）
defineExpose({ reload: load });


function normalized(item: ModuleRecord): Record<string, unknown> {
  return item.normalized && typeof item.normalized === 'object'
    ? item.normalized as Record<string, unknown>
    : {};
}

function rawStepCode(item: ModuleRecord): string {
  return String(item.stepCode ?? item.step_code ?? '').trim();
}

/**
 * 8D 步骤编号：库里是 d4:2 这种「步骤:序号」，还有一批 review:d1:12 的评审记录。
 * 界面上只显示 D4 / D1(评审) 这样人看得懂的形式。
 */
function stepLabel(item: ModuleRecord): string {
  const code = rawStepCode(item);
  if (!code) return '';
  const isReview = code.startsWith('review:');
  const step = (isReview ? code.slice('review:'.length) : code).split(':')[0] ?? '';
  const upper = step.toUpperCase();
  if (isReview) return `${upper}（评审）`;
  return props.stepOptions?.length ? eightDLabel(upper) : upper;
}

function extras(item: ModuleRecord): Array<[string, string]> {
  const source = normalized(item);
  return Object.entries(props.extraFields ?? {})
    .map(([key, label]) => [label, String(source[key] ?? '').trim()] as [string, string])
    .filter(([, value]) => value && value !== 'null');
}

function isCancelled(item: ModuleRecord): boolean {
  return String(item.status ?? '') === 'cancelled';
}

/**
 * 「已完成」全组件只有这一份判据。
 *
 * 用的就是下面 nextActions 认的那两个完成终态：同一条措施在按钮上显示
 * 「没有下一步了」、在组内进度里却不算完成，人只会当成 bug 报上来。
 * （后端 ACTION_DONE_STATUSES 还多认一个 'done'——那是旧库的写法，
 *   这套界面自己从来不写它；真要认得连 nextActions 一起改，不在这次范围内。）
 */
const DONE_STATUSES = ['completed', 'closed'];
function isDone(item: ModuleRecord): boolean {
  return DONE_STATUSES.includes(String(item.status ?? ''));
}

/* ---------- 新建 ---------- */

const addOpen = ref(false);
const addStep = ref('');
const addTitle = ref('');
const addOwner = ref('');
const addDue = ref('');
const addSaving = ref(false);

/**
 * 从 8D 报告点某个空缺的 D 步进来：直接展开表单并选好那一步。
 * 必须声明在 addStep 之后——写在文件上半部分会 ReferenceError。
 */
watch(() => props.presetStep, (step) => {
  if (!step) return;
  addStep.value = step;
  addOpen.value = true;
}, { immediate: true });

/** 同一 D 步内的下一个序号：已有 d4:1、d4:2 → d4:3 */
function nextStepCode(step: string): string {
  const prefix = step.toLowerCase();
  let max = 0;
  for (const item of items.value) {
    const code = rawStepCode(item).replace(/^review:/, '');
    const [head, seq] = code.split(':');
    if (head === prefix) max = Math.max(max, Number(seq ?? '1') || 1);
  }
  return `${prefix}:${max + 1}`;
}

async function createItem(): Promise<void> {
  if (!addTitle.value.trim()) {
    notify('error', '请填写内容', '步骤/措施的内容说明不能为空。');
    return;
  }
  if (props.stepOptions?.length && !addStep.value) {
    notify('error', '请选择步骤', '先选这条记录属于 D0–D8 的哪一步。');
    return;
  }
  addSaving.value = true;
  try {
    const payload: Record<string, unknown> = {
      parentId: Number(props.parentId),
      title: addTitle.value.trim(),
      status: 'pending',
    };
    if (props.stepOptions?.length) payload.stepCode = nextStepCode(addStep.value);
    if (addOwner.value.trim()) payload.ownerName = addOwner.value.trim();
    if (addDue.value) payload.dueAt = new Date(`${addDue.value}T23:59:59+08:00`).toISOString();
    await eqsApi.createModuleRecord(props.module, props.resource, payload);
    notify('success', '已添加', addTitle.value.trim());
    addOpen.value = false;
    addTitle.value = '';
    addOwner.value = '';
    addDue.value = '';
    await load();
    emit('changed');
  } catch (caught) {
    notify('error', '添加失败', errorMessage(caught));
  } finally {
    addSaving.value = false;
  }
}

/* ---------- 编辑 / 状态 ---------- */

const editingId = ref<string | number | null>(null);
const editTitle = ref('');
const editOwner = ref('');
const editSaving = ref(false);

function startEdit(item: ModuleRecord): void {
  editingId.value = item.id;
  editTitle.value = String(item.title ?? '');
  editOwner.value = String(item.owner_name ?? item.ownerName ?? '');
}

async function saveEdit(item: ModuleRecord): Promise<void> {
  if (!editTitle.value.trim()) {
    notify('error', '内容不能为空', `要清空这条记录请改用「${voidText.value}」。`);
    return;
  }
  editSaving.value = true;
  try {
    await eqsApi.updateModuleRecord(props.module, props.resource, item.id, {
      title: editTitle.value.trim(),
      ownerName: editOwner.value.trim() || undefined,
    });
    editingId.value = null;
    await load();
    emit('changed');
  } catch (caught) {
    notify('error', '保存失败', errorMessage(caught));
  } finally {
    editSaving.value = false;
  }
}

async function advance(item: ModuleRecord, status: string, label: string): Promise<void> {
  if (status === 'cancelled'
    && !await confirmAction({
      title: props.voidLabel
        ? `把「${item.title || stepLabel(item)}」设为「${voidText.value}」？`
        : `确认作废「${item.title || stepLabel(item)}」？`,
      danger: true,
    })) return;
  /*
    完成前先看有没有交付物证据（0826）：后端同一判据会拦，这里提前说清楚，
    省得人白跑一趟还以为按钮坏了。设「不适用」不需要证据。
  */
  if (status === 'completed' && props.withAttachments && attachmentCountOf(item) === 0) {
    notify('error', '还没挂交付物',
      `完成前先点「传附件」把产出物（文件 / 照片）传上来；这条确实不需要做的话设「${voidText.value}」。`);
    return;
  }
  /*
    先让外层把抽屉表单里没保存的东西存了。
    子条目的状态是直接写库的，而人刚在上面填的字段还在浏览器里——
    「断点实施」要求父变更的「实际断点」非空，人填了没存就点完成，
    后端读库当然是空的，报错却说「还是空的」，看着屏幕上明明写着（试用反馈 0819）。
  */
  if (props.beforeAdvance && !await props.beforeAdvance()) return;
  try {
    await eqsApi.updateModuleStatus(props.module, props.resource, item.id, status);
    notify('success', label, String(item.title ?? ''));
    await load();
    emit('changed');
  } catch (caught) {
    notify('error', `${label}失败`, errorMessage(caught));
  }
}

/** 当前状态可走的下一步；未知状态（旧系统迁来的）也给出口 */
function nextActions(item: ModuleRecord): Array<{ status: string; label: string }> {
  const status = String(item.status ?? '');
  if (isCancelled(item) || isDone(item)) return [];
  const actions: Array<{ status: string; label: string }> = [];
  if (status !== 'in_progress') actions.push({ status: 'in_progress', label: '开始' });
  actions.push({ status: 'completed', label: '完成' });
  actions.push({ status: 'cancelled', label: voidText.value });
  return actions;
}

/* ---------- 措施行的交付物附件（0826：完成前要证据） ---------- */

const attachFileInput = ref<HTMLInputElement | null>(null);
const attachTargetId = ref<string | number | null>(null);
const attachUploadingId = ref<string | number | null>(null);
/** 展开看附件清单的行；文件列表懒加载、按行缓存 */
const attachOpen = ref<Record<string, boolean>>({});
const attachFiles = ref<Record<string, Array<{ id: number; fileName: string; sizeBytes: number }>>>({});

/** 行上的 📎 数：列表接口给的 attachment_count（只有变更措施这种资源带） */
function attachmentCountOf(item: ModuleRecord): number {
  const raw = (item as Record<string, unknown>).attachment_count;
  return raw === null || raw === undefined ? 0 : Number(raw);
}

function pickAttachment(item: ModuleRecord): void {
  attachTargetId.value = item.id;
  attachFileInput.value?.click();
}

async function uploadAttachment(): Promise<void> {
  const id = attachTargetId.value;
  const file = attachFileInput.value?.files?.[0];
  if (id === null || !file || attachUploadingId.value !== null) return;
  attachUploadingId.value = id;
  try {
    const payload = new FormData();
    payload.append('file', file);
    await eqsApi.uploadModuleAttachment(props.module, props.resource, id, payload);
    notify('success', '交付物已上传', file.name);
    if (attachFileInput.value) attachFileInput.value.value = '';
    // 缓存作废让下次展开重取；行上的 📎 数靠重拉列表刷新
    const bag = { ...attachFiles.value };
    delete bag[String(id)];
    attachFiles.value = bag;
    await load();
  } catch (caught) {
    notify('error', '上传失败', errorMessage(caught));
  } finally {
    attachUploadingId.value = null;
  }
}

async function toggleAttachments(item: ModuleRecord): Promise<void> {
  const key = String(item.id);
  const next = !attachOpen.value[key];
  attachOpen.value = { ...attachOpen.value, [key]: next };
  if (!next || attachFiles.value[key]) return;
  try {
    const payload = await eqsApi.moduleAttachments(props.module, props.resource, item.id);
    attachFiles.value = {
      ...attachFiles.value,
      [key]: (payload.attachments ?? []) as Array<{ id: number; fileName: string; sizeBytes: number }>,
    };
  } catch {
    attachFiles.value = { ...attachFiles.value, [key]: [] };
  }
}

function attachmentUrl(item: ModuleRecord, attachmentId: number): string {
  return eqsApi.moduleAttachmentDownloadUrl(props.module, props.resource, item.id, attachmentId);
}

/* ---------- 分组显示 ---------- */

/*
  组名一律不编号。变更第 4 步的 exit 是程序文件原话「五类落地工作」，
  而实际分组数和它对不上（还多一个兜底的「其他措施」，0827 起五个阶段
  又拆在第 4、5 两步）。编成「一、二…」会让人以为程序文件里有那么多类，
  所以只写名字不写序号。
*/
/** 动作编号：正常在顶层 code 上，个别老数据只在 normalized 里留了一份 */
function codeOf(item: ModuleRecord): string {
  return String(item.code ?? normalized(item).code ?? '').trim().toUpperCase();
}

interface ChildSection {
  key: string;
  /** null = 不分组，就是原来那一条平铺列表 */
  name: string | null;
  note: string;
  items: ModuleRecord[];
  emptyText: string;
}

/**
 * 收起时那一行右边的进度。
 *
 * 分母**不含作废**：留着会把进度永远卡在 N-1/N，
 * 而「这条这次不用做，作废并写清原因」正是第 4 步 hint 里教人做的事，
 * 不能反过来罚他。作废掉几条另写出来——分母无声变小看着像丢数据。
 */
function progressOf(items: ModuleRecord[]): string {
  const live = items.filter((item) => !isCancelled(item));
  const voided = items.length - live.length;
  if (!live.length) return voided ? `${voided} 条已${voidText.value}` : '0 条';
  return `${live.filter(isDone).length}/${live.length}${voided ? ` · ${voidText.value} ${voided}` : ''}`;
}

const sections = computed<ChildSection[]>(() => {
  const groups = props.codeGroups ?? [];
  if (!groups.length) {
    return [{ key: 'all', name: null, note: '', items: items.value, emptyText: props.emptyHint }];
  }
  // 归别的步管的，这一屏根本不显示——它们不是「未归组」
  const foreign = new Set((props.foreignCodes ?? []).map((code) => code.trim().toUpperCase()));
  const mine = foreign.size
    ? items.value.filter((item) => !foreign.has(codeOf(item)))
    : items.value;
  return groups.map((group) => {
    const codes = new Set(group.codes.map((code) => code.trim().toUpperCase()).filter(Boolean));
    return {
      key: `group:${group.name}`,
      name: group.name,
      note: '',
      items: mine.filter((item) => codes.has(codeOf(item))),
      emptyText: '这一组这次没有要做的动作。',
    };
  });
});

/** 「共 N 条」按真正显示出来的算：第 5 步只显示自己那一组，报总数会对不上 */
const shownCount = computed(() => new Set(
  sections.value.flatMap((section) => section.items.map((item) => String(item.id))),
).size);

/* ---------- 分组折叠 ---------- */

/*
  分组默认收起，一组只占一行「组名 + 进度」（0824 Tony：第 4 步还是太长）。
  6 组 41 条一次铺开，人得滚三屏才看得到底下的按钮。

  只有传了 codeGroups 才有折叠这回事：不传时 sections 只有一段、name 为 null，
  isGroupOpen 直接返回 true，渲染出来和折叠之前一字不差（8D 和别的模块在用）。
*/

/** 人手点过的组：key → 展开与否。没点过的组由 autoOpenKey 决定 */
const groupToggled = ref<Record<string, boolean>>({});
/**
 * 默认展开哪一组：**第一组有未完成条目的**。
 *
 * 全收起最短，但点开第 4 步只看到 6 行标题、一条动作都看不见，
 * 等于替人多加一层点击——而第 4 步本来就是要人干活的那一步。
 * 展开第一组还没做完的，等于把「下一件该干的事」直接摆出来。
 * 全做完时一组都不展开：没有要干的事了，那时候人要的就是六行进度。
 *
 * 只认一次，之后不再跟着数据动：勾完这一组的最后一条就当场把这一组收走、
 * 弹开下一组，手正放在上面的人会被晃到。换记录时清空重认（见下面的 watch）。
 */
const autoOpenKey = ref<string | null>(null);

/** 还欠着的：既没做完也没作废 */
function isPending(item: ModuleRecord): boolean {
  return !isDone(item) && !isCancelled(item);
}

// 换一条记录/换一份清单：折叠状态跟着重来，否则新记录会沿用上一条的展开位置
watch(() => [props.module, props.resource, props.parentId], () => {
  groupToggled.value = {};
  autoOpenKey.value = null;
});

watch(sections, (list) => {
  // 认过就不再改。还没东西可展开时**不落定**——「生成执行清单」是先空后有，
  // 落定成「无」会让 41 条铺出来的那一刻全是收起的
  if (autoOpenKey.value !== null) return;
  const first = list.find((section) => section.name && section.items.some(isPending));
  if (first) autoOpenKey.value = first.key;
});

function isGroupOpen(section: ChildSection): boolean {
  if (!section.name) return true;
  return groupToggled.value[section.key] ?? section.key === autoOpenKey.value;
}

function toggleGroup(section: ChildSection): void {
  groupToggled.value = { ...groupToggled.value, [section.key]: !isGroupOpen(section) };
}
</script>

<template>
  <section class="child-list" :class="{ 'child-list-embedded': hideTitle }">
    <header>
      <b v-if="!hideTitle">{{ title }}</b>
      <div class="child-header-side">
        <small v-if="shownCount">共 {{ shownCount }} 条</small>
        <button
          v-if="editable && !addOpen"
          class="inline-button"
          type="button"
          @click="addOpen = true; addStep = stepOptions?.[0] ?? ''"
        >＋ 添加</button>
      </div>
    </header>

    <form v-if="editable && addOpen" class="child-add" @submit.prevent="createItem">
      <select v-if="stepOptions?.length" v-model="addStep">
        <option v-for="option in stepOptions" :key="option" :value="option">{{ optionLabel(option) }}</option>
      </select>
      <p v-if="stepGuide" class="child-step-help">{{ stepGuide.help }}</p>
      <!-- 根因分析、永久措施动辄一两百字，单行框根本装不下 -->
      <textarea
        v-model="addTitle"
        class="child-add-content"
        rows="3"
        :placeholder="stepGuide?.placeholder || '内容说明 *'"
      ></textarea>
      <input v-model="addOwner" type="text" placeholder="责任人" />
      <input v-model="addDue" type="date" title="完成期限" />
      <div class="child-add-actions">
        <button class="button button-primary" type="submit" :disabled="addSaving">
          {{ addSaving ? '保存中…' : '保存' }}
        </button>
        <button class="inline-button" type="button" @click="addOpen = false">取消</button>
      </div>
    </form>

    <p v-if="loading" class="child-hint">正在读取…</p>
    <p v-else-if="error" class="child-hint child-error">读取失败：{{ error }}</p>
    <p v-else-if="!items.length" class="child-hint">{{ emptyHint }}</p>

    <!--
      不传 codeGroups 时 sections 只有一段、name 为 null，
      渲染出来和分组之前的 DOM 一模一样（8D 和别的模块在用，不许回归）。
    -->
    <template v-else>
      <template v-for="section in sections" :key="section.key">
        <!--
          收起时一组只占这一行：组名 + 进度。做成按钮而不是一行带下划线的字——
          没边框的文字看不出能点（0823 起全站的规矩）。
        -->
        <button
          v-if="section.name"
          class="child-group"
          type="button"
          :aria-expanded="isGroupOpen(section)"
          @click="toggleGroup(section)"
        >
          <i aria-hidden="true">{{ isGroupOpen(section) ? '▾' : '▸' }}</i>
          <span>{{ section.name }}</span>
          <small>{{ progressOf(section.items) }}</small>
        </button>

        <!-- 不分组时 isGroupOpen 恒为 true，这一层不产生任何 DOM -->
        <template v-if="isGroupOpen(section)">
          <p v-if="section.note" class="child-group-note">{{ section.note }}</p>
          <p v-if="section.name && !section.items.length" class="child-hint">{{ section.emptyText }}</p>
          <ol v-else class="child-items">
            <li v-for="item in section.items" :key="String(item.id)" :class="{ 'is-cancelled': isCancelled(item) }">
              <div class="child-head">
                <b v-if="stepLabel(item)" class="child-step">{{ stepLabel(item) }}</b>
                <template v-if="editingId === item.id">
                  <input v-model="editTitle" class="child-edit-input" type="text" />
                </template>
                <span v-else class="child-title">{{ item.title || item.code || '（无标题）' }}</span>
                <!-- 改叫「不适用」的清单里，cancelled 不能再显示成「已取消」——一个概念一个名字 -->
                <span v-if="isCancelled(item) && props.voidLabel" class="child-void-chip">{{ voidText }}</span>
                <StatusChip v-else-if="item.status" :value="String(item.status)" />
              </div>
              <dl v-if="extras(item).length" class="child-extra">
                <template v-for="[label, value] in extras(item)" :key="label">
                  <dt>{{ label }}</dt><dd>{{ value }}</dd>
                </template>
              </dl>
              <div class="child-foot">
                <template v-if="editingId === item.id">
                  <input v-model="editOwner" class="child-edit-owner" type="text" placeholder="责任人" />
                  <button class="inline-button" type="button" :disabled="editSaving" @click="saveEdit(item)">
                    {{ editSaving ? '保存中…' : '保存' }}
                  </button>
                  <button class="inline-button" type="button" @click="editingId = null">取消</button>
                </template>
                <template v-else>
                  <small v-if="item.owner_name || item.ownerName" class="child-owner">
                    责任人：{{ item.owner_name || item.ownerName }}
                  </small>
                  <!-- 交付物证据（0826）：📎 数一直可见（含已完成的行，追溯要看得到），点开看清单 -->
                  <button
                    v-if="props.withAttachments"
                    class="inline-button child-attach-count"
                    :class="{ 'child-attach-missing': attachmentCountOf(item) === 0 && !isCancelled(item) && !isDone(item) }"
                    type="button"
                    :title="attachmentCountOf(item) ? '查看已挂的交付物' : '还没挂交付物——完成前必须传'"
                    @click="toggleAttachments(item)"
                  >📎 {{ attachmentCountOf(item) }}</button>
                  <span v-if="editable && !isCancelled(item)" class="child-actions">
                    <button
                      v-if="props.withAttachments && !isDone(item)"
                      class="inline-button" type="button"
                      :disabled="attachUploadingId !== null"
                      @click="pickAttachment(item)"
                    >{{ attachUploadingId === item.id ? '上传中…' : '传附件' }}</button>
                    <button class="inline-button" type="button" @click="startEdit(item)">修改</button>
                    <button
                      v-for="action in nextActions(item)"
                      :key="action.status"
                      class="inline-button"
                      :class="{ 'child-danger': action.status === 'cancelled' }"
                      type="button"
                      @click="advance(item, action.status, action.label)"
                    >{{ action.label }}</button>
                  </span>
                </template>
              </div>
              <!--
                行内面板（0826）：某些措施底下要挂它自己的表单（第 4 步的库存清点表）。
                做成插槽而不是把表格写进这个通用组件——8D、问题清单也在用它，
                往里塞变更专用的东西迟早互相绊住。不传插槽时一个 DOM 都不产生。
              -->
              <slot name="item-panel" :item="item"></slot>
              <!-- 展开的交付物清单：懒加载，点 📎 才取 -->
              <ul v-if="props.withAttachments && attachOpen[String(item.id)]" class="child-files">
                <li v-if="!(attachFiles[String(item.id)]?.length)" class="child-files-empty">
                  还没有交付物附件。
                </li>
                <li v-for="file in attachFiles[String(item.id)] ?? []" :key="file.id">
                  <a :href="attachmentUrl(item, file.id)" target="_blank" rel="noopener">{{ file.fileName }}</a>
                </li>
              </ul>
            </li>
          </ol>
        </template>
      </template>
    </template>
    <!-- 共用一个隐藏文件框：点哪一行的「传附件」就传到哪一行（attachTargetId 记着） -->
    <input
      v-if="props.withAttachments"
      ref="attachFileInput" type="file" class="child-hidden-file"
      @change="uploadAttachment"
    >
  </section>
</template>

<style scoped>
.child-list { margin-top: 14px; padding-top: 14px; border-top: 1px solid var(--line); }
.child-list > header { display: flex; align-items: baseline; justify-content: space-between; }
.child-list > header b { font-size: 14px; }
.child-header-side { display: flex; align-items: baseline; gap: 10px; }
.child-header-side small { color: var(--muted); font-size: 12px; }
.child-add {
  display: grid; grid-template-columns: minmax(150px, auto) 1fr auto; gap: 8px;
  margin-top: 10px; padding: 10px; border: 1px dashed var(--line); border-radius: var(--radius-s);
  align-items: center;
}
.child-add input, .child-add select { padding: 5px 8px; border: 1px solid var(--line); border-radius: 4px; font-size: 12px; }
.child-add > select { grid-column: 1; }
/* 这一步要填什么，就写在选框旁边，别让人选完还得猜 */
.child-step-help { grid-column: 2 / -1; margin: 0; color: var(--muted); font-size: 12px; line-height: 1.6; }
.child-add-content {
  grid-column: 1 / -1; padding: 7px 9px; border: 1px solid var(--line); border-radius: 4px;
  font: inherit; font-size: 12px; line-height: 1.75; resize: vertical;
}
.child-add-content::placeholder { color: #9aa5b1; }
.child-add-actions { grid-column: 1 / -1; display: flex; gap: 10px; align-items: center; }
.child-hint { margin: 8px 0 0; color: var(--muted); font-size: 12px; line-height: 1.7; }
/* 嵌进变更第 4 步的展开区：标题和上边框由外层那块负责，这里不再画一条分隔线 */
.child-list-embedded { margin-top: 6px; padding-top: 0; border-top: 0; }
.child-list-embedded > header { justify-content: flex-end; }
/*
  组名只写名字不编号——程序文件的「五类落地工作」只有五类，编号会让人以为有第六类。
  这一行现在是折叠开关：画成有边框的按钮，一行没框的字看不出能点。
*/
.child-group {
  display: flex; align-items: baseline; gap: 8px; width: 100%;
  margin: 10px 0 0; padding: 7px 10px;
  border: 1px solid var(--line); border-radius: var(--radius-s);
  background: var(--white); font: inherit; font-size: 13px; text-align: left; cursor: pointer;
}
.child-group:hover { border-color: var(--blue); }
.child-group > i { flex: 0 0 auto; color: var(--muted); font-style: normal; font-size: 11px; }
.child-group > span { flex: 1; min-width: 0; font-weight: 800; color: var(--ink); }
.child-group > small { flex: 0 0 auto; color: var(--muted); font-size: 12px; font-weight: 400; }
.child-group-note { margin: 3px 0 0; color: var(--muted); font-size: 12px; line-height: 1.6; }
.child-error { color: var(--risk); }
.child-items { margin: 10px 0 0; padding: 0; list-style: none; display: grid; gap: 10px; }
.child-items li { padding: 9px 11px; border: 1px solid var(--line); border-radius: var(--radius-s); background: var(--white); }
.child-items li.is-cancelled { opacity: .55; }
.child-items li.is-cancelled .child-title { text-decoration: line-through; }
.child-head { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.child-step {
  padding: 1px 7px; border-radius: 4px; background: rgba(0, 117, 174, .1);
  color: var(--blue); font-size: 12px; font-weight: 800;
}
.child-title { flex: 1; min-width: 0; color: var(--ink); font-size: 13px; line-height: 1.6; }
.child-edit-input { flex: 1; min-width: 0; padding: 4px 8px; border: 1px solid var(--blue); border-radius: 4px; font-size: 13px; }
.child-edit-owner { width: 110px; padding: 3px 8px; border: 1px solid var(--line); border-radius: 4px; font-size: 12px; }
.child-extra {
  display: grid; grid-template-columns: auto 1fr; gap: 2px 10px;
  margin: 6px 0 0; color: var(--muted); font-size: 12px;
}
.child-extra dt { font-weight: 700; }
.child-extra dd { margin: 0; }
.child-foot { display: flex; align-items: center; gap: 10px; margin-top: 5px; flex-wrap: wrap; }
.child-owner { color: var(--muted); font-size: 12px; }
.child-actions { display: flex; gap: 8px; margin-left: auto; }
.child-danger { color: var(--risk); }
/* 「不适用」的标签：灰而不红——它是正常处置，不是错误态 */
.child-void-chip {
  padding: 1px 8px; border-radius: 999px;
  background: rgba(120, 128, 138, .14); color: #5a636e; font-size: 11px;
}
/* 交付物 📎（0826）：没证据的行标黄提醒——完成门禁认的就是它 */
.child-attach-count { font-variant-numeric: tabular-nums; }
.child-attach-missing { border-color: rgba(184, 134, 11, .55); color: #8a6508; }
.child-files { margin: 6px 0 0; padding: 6px 10px; list-style: none; display: grid; gap: 3px; border-left: 3px solid var(--line); font-size: 12px; }
.child-files-empty { color: var(--muted); }
.child-hidden-file { display: none; }
</style>
